import React from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faKey, faShieldHalved } from "@fortawesome/pro-solid-svg-icons";

import { Pill, PillVariants } from "src/base-components/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import {
  BYOK_TOOLTIP,
  REGION_BADGES,
  TAKTILE_HOSTED_TOOLTIP,
} from "src/genai/ModelSelector/constants";
import { ModelOption } from "src/genai/ModelSelector/types";

type ResidencyPillProps = {
  icon: IconProp;
  label: string;
  variant: PillVariants;
  tooltip?: React.ReactNode;
};

const ResidencyPill: React.FC<ResidencyPillProps> = ({
  icon,
  label,
  variant,
  tooltip,
}) => {
  const pill = (
    <Pill size="sm" variant={variant}>
      <Pill.Icon icon={icon} />
      <Pill.Text>{label}</Pill.Text>
    </Pill>
  );

  if (!tooltip) return pill;

  return (
    <Tooltip body={tooltip} placement="top" asChild>
      {pill}
    </Tooltip>
  );
};

/**
 * The "Your key" badge for BYOK deployments.
 */
export const ByokBadge: React.FC<{ model: ModelOption }> = ({ model }) =>
  model.byok ? (
    <ResidencyPill
      icon={faKey}
      label="Your key"
      tooltip={BYOK_TOOLTIP}
      variant="outlined-white"
    />
  ) : null;

/**
 * The residency-slot badges: Taktile-hosted + region for managed models. BYOK
 * deployments route through the customer's own provider, so their residency
 * isn't ours to assert — the "Your key" badge stands in instead.
 */
export const ResidencyBadges: React.FC<{ model: ModelOption }> = ({
  model,
}) => {
  if (model.byok) return <ByokBadge model={model} />;

  const regionBadge = model.region ? REGION_BADGES[model.region] : undefined;

  return (
    <>
      {regionBadge && (
        <ResidencyPill
          icon={regionBadge.icon}
          label={regionBadge.label}
          variant={regionBadge.variant}
        />
      )}
      {model.hosting === "aws" && (
        <ResidencyPill
          icon={faShieldHalved}
          label="Taktile"
          tooltip={TAKTILE_HOSTED_TOOLTIP}
          variant="indigo-light"
        />
      )}
    </>
  );
};
